import { useState, useEffect, useRef } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Heart, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { likeTip, shareTip, viewTip } from '../api/tipsApi';
import { analyticsService } from '@/services/analyticsService';

interface NativeFilesystemPlugin {
  writeFile(options: { path: string; data: string; directory: string; recursive?: boolean }): Promise<{ uri: string }>;
  deleteFile(options: { path: string; directory: string }): Promise<void>;
}

interface NativeSharePlugin {
  share(options: { title?: string; text?: string; files?: string[]; dialogTitle?: string }): Promise<void>;
}

const Filesystem = registerPlugin<NativeFilesystemPlugin>('Filesystem');
const Share = registerPlugin<NativeSharePlugin>('Share');

const tipShareText = `بوکا؛ دنیای جدید کتاب ها

📥 دانلود از بازار : https://cafebazaar.ir/app/ir.booka.app
📥 دانلود از مایکت : https://myket.ir/app/ir.booka.app`;

interface TipItemProps {
  tipId: number;
  content: string;
  title: string;
  color: string;
  isActive: boolean;
  bookId?: number;
  coverImage?: string;
  bookCategory?: string;
  contentType: string;
  likesCount: number;
  sharesCount: number;
  isLiked: boolean;
}

const TipItem = ({
  tipId,
  content,
  title,
  color,
  isActive,
  bookId,
  coverImage,
  bookCategory,
  contentType,
  likesCount,
  sharesCount,
  isLiked: initialIsLiked,
}: TipItemProps) => {
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [currentLikesCount, setCurrentLikesCount] = useState(likesCount);
  const [currentSharesCount, setCurrentSharesCount] = useState(sharesCount);
  const viewedRef = useRef(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  useEffect(() => {
    if (isActive && !viewedRef.current) {
      const timer = setTimeout(() => {
        viewTip(tipId).then(() => {
          void analyticsService.logEvent('view_tip', {
            tip_id: String(tipId),
            book_id: bookId ? String(bookId) : undefined,
            book_title: title,
            book_category: bookCategory || 'unknown',
            content_type: contentType,
          });
        }).catch(console.error);
        viewedRef.current = true;
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isActive, tipId]);

  const handleBookClick = () => {
    if (bookId) {
      navigate(`/library/book`, { state: { bookId, source: 'tip' } });
    }
  };

  const handleLike = async () => {
    setIsLiked(!isLiked);
    setCurrentLikesCount((prev) => (isLiked ? prev - 1 : prev + 1));
    try {
      const result = await likeTip(tipId);
      void analyticsService.logEvent(result.liked ? 'like_tip' : 'unlike_tip', {
        tip_id: String(tipId),
        book_id: bookId ? String(bookId) : undefined,
        book_title: title,
        book_category: bookCategory || 'unknown',
        content_type: contentType,
      });
    } catch {
      setIsLiked(isLiked);
      setCurrentLikesCount(likesCount);
    }
  };

  const isShareCanceled = (error: unknown): boolean => {
    if (!error) return false;
    if (typeof error === 'object') {
      const maybeError = error as { message?: string; name?: string };
      const message = (maybeError.message || '').toLowerCase();
      const name = (maybeError.name || '').toLowerCase();
      return (
        name.includes('abort') ||
        message.includes('share canceled') ||
        message.includes('share cancelled') ||
        message.includes('cancelled') ||
        message.includes('canceled')
      );
    }
    return String(error).toLowerCase().includes('share canceled');
  };

  const blobToBase64 = async (blob: Blob): Promise<string> => {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read image data'));
      reader.readAsDataURL(blob);
    });

    const base64 = dataUrl.split(',')[1];
    if (!base64) {
      throw new Error('Failed to encode image');
    }

    return base64;
  };

  const recordShareSuccess = async () => {
    try {
      await shareTip(tipId);
      setCurrentSharesCount((prev) => prev + 1);
      void analyticsService.logEvent('share_tip', {
        tip_id: String(tipId),
        book_id: bookId ? String(bookId) : undefined,
        book_title: title,
        book_category: bookCategory || 'unknown',
        content_type: contentType,
      });
    } catch (error) {
      console.warn('Failed to record share', error);
    }
  };

  const shareViaNativeSheet = async (blob: Blob) => {
    const Directory = { Cache: 'CACHE' } as const;
    const fileName = `tip-${tipId}-${Date.now()}.png`;
    const base64 = await blobToBase64(blob);
    const result = await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Cache,
      recursive: true,
    });

    try {
      await Share.share({
        title,
        text: tipShareText,
        files: [result.uri],
        dialogTitle: 'اشتراک‌گذاری نکته',
      });
      await recordShareSuccess();
    } finally {
      await Filesystem.deleteFile({
        path: fileName,
        directory: Directory.Cache,
      }).catch(() => {});
    }
  };

  const shareViaWebSheet = async (blob: Blob) => {
    const file = new File([blob], `tip-${tipId}.png`, { type: 'image/png' });
    const nav = navigator as Navigator & {
      canShare?: (data: ShareData) => boolean;
      share?: (data?: ShareData) => Promise<void>;
    };

    if (typeof nav.share !== 'function') {
      throw new Error('Sharing is not supported on this device');
    }

    if (!nav.canShare || nav.canShare({ files: [file] })) {
      await nav.share({
        title,
        text: tipShareText,
        files: [file],
      });
    } else {
      await nav.share({
        title,
        text: tipShareText,
      });
    }

    await recordShareSuccess();
  };

  const createShareImage = async (): Promise<Blob> => {
    const colorMap: Record<string, string> = {
      'bg-blue-100': '#dbeafe',
      'bg-green-100': '#dcfce7',
      'bg-amber-100': '#fef3c7',
      'bg-rose-100': '#ffe4e6',
      'bg-purple-100': '#f3e8ff',
      'bg-cyan-100': '#cffafe',
      'bg-orange-100': '#ffedd5',
      'bg-pink-100': '#fce7f3',
    };
    const bgColor = colorMap[color] || '#f3f4f6';

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');

    const paddingX = 50;
    const paddingY = 100;
    const maxWidth = 540;
    const contentWidth = maxWidth - paddingX * 2;
    const fontSize = 26;
    const lineHeight = fontSize * 2.2;
    const bookTitleFontSize = 24;
    const footerFontSize = 18;
    const borderWidth = 3;
    const borderRadius = 16;
    const borderPadding = 20;

    ctx.font = `600 ${fontSize}px Vazirmatn, sans-serif`;
    const words = content.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > contentWidth) {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);

    const textHeight = lines.length * lineHeight;
    const titleToContentGap = 120;
    const contentToFooterGap = 140;
    const canvasHeight =
      paddingY + bookTitleFontSize + titleToContentGap + textHeight + contentToFooterGap + footerFontSize + paddingY;
    const canvasWidth = maxWidth;

    canvas.width = canvasWidth * 2;
    canvas.height = canvasHeight * 2;
    ctx.scale(2, 2);

    ctx.direction = 'rtl';

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    ctx.strokeStyle = '#374151';
    ctx.lineWidth = borderWidth;
    ctx.beginPath();
    const bx = borderPadding;
    const by = borderPadding;
    const bw = canvasWidth - borderPadding * 2;
    const bh = canvasHeight - borderPadding * 2;
    ctx.moveTo(bx + borderRadius, by);
    ctx.lineTo(bx + bw - borderRadius, by);
    ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + borderRadius);
    ctx.lineTo(bx + bw, by + bh - borderRadius);
    ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - borderRadius, by + bh);
    ctx.lineTo(bx + borderRadius, by + bh);
    ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - borderRadius);
    ctx.lineTo(bx, by + borderRadius);
    ctx.quadraticCurveTo(bx, by, bx + borderRadius, by);
    ctx.closePath();
    ctx.stroke();

    const titleFontSize = Math.round(fontSize * 0.85);
    ctx.fillStyle = '#374151';
    ctx.font = `700 ${titleFontSize}px Vazirmatn, sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillText(title, canvasWidth - paddingX, paddingY);

    const quoteSize = 50;
    ctx.font = `400 ${quoteSize}px Georgia, serif`;
    ctx.fillStyle = '#9ca3af';
    ctx.textAlign = 'right';
    const contentStartY = paddingY + bookTitleFontSize + titleToContentGap;
    ctx.fillText('"', canvasWidth - paddingX + 10, contentStartY - 45);

    ctx.font = `600 ${fontSize}px Vazirmatn, sans-serif`;
    ctx.fillStyle = '#374151';

    lines.forEach((line, index) => {
      const lineWords = line.split(' ');
      const isLastLine = index === lines.length - 1;

      if (isLastLine || lineWords.length === 1) {
        ctx.textAlign = 'right';
        ctx.fillText(line, canvasWidth - paddingX, contentStartY + index * lineHeight);
      } else {
        let totalWordsWidth = 0;
        for (const word of lineWords) {
          totalWordsWidth += ctx.measureText(word).width;
        }

        const totalSpaceNeeded = contentWidth - totalWordsWidth;
        const spaceWidth = totalSpaceNeeded / (lineWords.length - 1);

        ctx.textAlign = 'right';
        let xPos = canvasWidth - paddingX;

        for (let i = 0; i < lineWords.length; i++) {
          ctx.fillText(lineWords[i], xPos, contentStartY + index * lineHeight);
          if (i < lineWords.length - 1) {
            xPos -= ctx.measureText(lineWords[i]).width + spaceWidth;
          }
        }
      }
    });

    ctx.font = `400 ${quoteSize}px Georgia, serif`;
    ctx.fillStyle = '#9ca3af';
    ctx.textAlign = 'left';
    const lastLineY = contentStartY + (lines.length - 1) * lineHeight;
    ctx.fillText('"', paddingX - 10, lastLineY + 25);

    const separatorY = canvasHeight - paddingY - footerFontSize - 35;
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(paddingX + 50, separatorY);
    ctx.lineTo(canvasWidth - paddingX - 50, separatorY);
    ctx.stroke();

    const footerY = canvasHeight - paddingY;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#374151';

    const footerPart1 = 'برای تجربه متفاوت کتاب خواندن ';
    const footerBold = 'بوکا';
    const footerPart2 = ' رو نصب کن';

    ctx.font = `400 ${footerFontSize}px Vazirmatn, sans-serif`;
    const part1Width = ctx.measureText(footerPart1).width;
    ctx.font = `700 ${footerFontSize}px Vazirmatn, sans-serif`;
    const boldWidth = ctx.measureText(footerBold).width;
    ctx.font = `400 ${footerFontSize}px Vazirmatn, sans-serif`;
    const part2Width = ctx.measureText(footerPart2).width;

    const totalWidth = part1Width + boldWidth + part2Width;
    let startX = canvasWidth / 2 + totalWidth / 2;

    ctx.textAlign = 'right';
    ctx.font = `400 ${footerFontSize}px Vazirmatn, sans-serif`;
    ctx.fillText(footerPart1, startX, footerY);
    startX -= part1Width;

    ctx.font = `700 ${footerFontSize}px Vazirmatn, sans-serif`;
    ctx.fillText(footerBold, startX, footerY);
    startX -= boldWidth;

    ctx.font = `400 ${footerFontSize}px Vazirmatn, sans-serif`;
    ctx.fillText(footerPart2, startX, footerY);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/png', 1.0);
    });

    if (!blob) {
      throw new Error('Failed to create image');
    }

    return blob;
  };

  const handleShare = async () => {
    if (isCapturing) return;

    setIsCapturing(true);
    setShareError(null);

    try {
      const blob = await createShareImage();

      if (Capacitor.isNativePlatform()) {
        await shareViaNativeSheet(blob);
      } else {
        await shareViaWebSheet(blob);
      }
    } catch (error) {
      if (isShareCanceled(error)) {
        return;
      }

      console.error('Failed to share tip image:', error);
      setShareError('اشتراک‌گذاری مستقیم در دسترس نیست.');
      setTimeout(() => setShareError(null), 2500);
    } finally {
      setIsCapturing(false);
    }
  };

  const formatCount = (count: number): string => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toLocaleString('fa-IR');
  };

  return (
    <div
      className="w-full snap-start snap-always relative flex-shrink-0 overflow-hidden"
      style={{ height: '100dvh' }}
    >
      <div className={cn('absolute inset-0', color)} />

      {isCapturing && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-white border-t-transparent"></div>
        </div>
      )}

      {shareError && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-persian">
            {shareError}
          </div>
        </div>
      )}

      <div className="relative h-full w-full flex flex-col">
        <div className="flex-1 px-4 pt-24 pb-28">
          <p
            className="text-base sm:text-lg font-semibold text-gray-700 px-2"
            dir="rtl"
            style={{
              lineHeight: '2.45',
              textAlign: 'justify',
              textAlignLast: 'right',
            }}
          >
            {content}
          </p>
        </div>

        <div className="absolute bottom-10 left-0 right-0 px-4" dir="ltr">
          <div className="flex items-end justify-between">
            <button onClick={handleBookClick} className="flex items-center gap-2 transition-opacity hover:opacity-80">
              <div className="w-[84px] h-[124px] rounded-lg overflow-hidden bg-white/30 flex-shrink-0 shadow-md">
                {coverImage ? (
                  <img src={coverImage} alt={title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-lg bg-white/50">📚</div>
                )}
              </div>
              <span className="text-sm font-medium text-gray-600 max-w-[120px] line-clamp-2 text-right" dir="rtl">
                {title}
              </span>
            </button>

            <div className="flex flex-col gap-3">
              <button onClick={handleLike} className="flex flex-col items-center justify-center transition-transform active:scale-90">
                <Heart
                  className={cn(
                    'w-7 h-7 transition-colors',
                    isLiked ? 'text-red-500 fill-red-500' : 'text-gray-600'
                  )}
                />
                <span className="text-gray-600 text-xs font-medium mt-1">{formatCount(currentLikesCount)}</span>
              </button>
              <button onClick={handleShare} className="flex flex-col items-center justify-center transition-transform active:scale-90">
                <Share2 className="w-7 h-7 text-gray-600" />
                <span className="text-gray-600 text-xs font-medium mt-1">{formatCount(currentSharesCount)}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TipItem;
